/**
 * Selection state for the T4-135 ambiguous-match review, kept free of React so the rules that
 * decide what gets linked are testable on their own.
 *
 * Keyed by email, which is also how the backend groups: a Xero customer has one primary email and
 * a client's primary contact has one, so an email identifies a group uniquely.
 *
 * Nothing is pre-selected anywhere in here, deliberately. Pre-selecting would let a user confirm
 * by reflex a link nobody chose, and the candidate order is ORDER BY client.id - an artefact, not
 * a signal about which client is right. The backend re-checks every pairing against fresh data
 * regardless; what is here is the explanation, not the guard.
 */
export const groupKey = group => group.email;

/**
 * The pairings a group's current selection has decided, in the shape the mutation carries.
 *
 * One place answers "what has this group decided", so the badge and the import can never drift on
 * it - review flagged the two re-implementing the same per-kind `typeof` check, which is the class
 * of split this PR existed to close in the first place.
 */
const pairingsFor = (group, chosen) => {
	if (!chosen) return [];

	if (group.kind === 'MANY_CLIENTS') {
		return typeof chosen.clientId === 'number'
			? [{xeroContactId: group.xeroContacts[0].xeroContactId, clientId: chosen.clientId}]
			: [];
	}

	if (group.kind === 'MANY_CONTACTS') {
		return typeof chosen.xeroContactId === 'string'
			? [{xeroContactId: chosen.xeroContactId, clientId: group.clients[0].clientId}]
			: [];
	}

	return group.clients.flatMap(client => {
		const xeroContactId = (chosen.byClientId || {})[client.clientId];

		return xeroContactId ? [{xeroContactId, clientId: client.clientId}] : [];
	});
};

/**
 * Whether a group has nothing left to decide - the card's RESOLVED badge, and the footer's count.
 *
 * The single-decision shapes are resolved once they have produced their pairing. A many-to-many
 * needs every client decided, and "do not link this client" is a decision: a group where all of
 * them are declined is finished, and reads RESOLVED, even though it contributes no pairing.
 */
export const isGroupResolved = (group, selection) => {
	const chosen = selection[groupKey(group)];

	if (!chosen) return false;

	if (group.kind === 'MANY_TO_MANY') {
		const byClientId = chosen.byClientId || {};
		return group.clients.every(client => byClientId[client.clientId] !== undefined);
	}

	return pairingsFor(group, chosen).length === 1;
};

/** How many cards are finished. The footer's numerator, and the same predicate as the badge. */
export const confirmedCount = (groups, selection) => groups.filter(group => isGroupResolved(group, selection)).length;

/** The name of the other client in this group already holding this contact, or null. */
export const contactTakenBy = (group, selection, xeroContactId, forClientId) => {
	const byClientId = (selection[groupKey(group)] || {}).byClientId || {};

	const holder = group.clients.find(
		client => client.clientId !== forClientId && byClientId[client.clientId] === xeroContactId
	);

	return holder ? holder.name : null;
};

/**
 * The confirmed pairings, in the shape the mutation carries.
 *
 * Only links are emitted. "Do not link this client", an unchosen customer in a MANY_CONTACTS
 * group and an untouched group all mean the same thing to the backend - absent, which it reads
 * as IGNORE - so there is no declined state here to encode and no branch to get wrong.
 *
 * A pairing counts as soon as it is made, not once its whole group is settled. The criterion
 * added 2026-09-15 enables the import on any confirmed one-to-one mapping "manually resolved by
 * the user in a mismatch group", and in a many-to-many that is true of the first client paired
 * while the second is still undecided. Gating on `isGroupResolved` here - as an earlier cut did -
 * would have left that pairing uncounted and the button dead. `isGroupResolved` still decides the
 * card's RESOLVED badge, which is a different question: whether anything is left to decide.
 */
export const toResolutions = (groups, selection) => groups.flatMap(group => pairingsFor(group, selection[groupKey(group)]));

/**
 * How many contacts pressing Import will actually bring in, and therefore whether it can be
 * pressed at all (T4-135).
 *
 * Three sources: the contacts matched to one client without asking, the ones that become brand new
 * clients, and every pairing the user has confirmed so far. It rises as they resolve.
 *
 * The new-client half is not decoration. Counting only matches - as the criterion's wording
 * suggests - left a batch of nineteen unmatched contacts plus one fully-declined group at zero,
 * which disabled the button permanently and stranded the nineteen with no way forward but to go
 * back and deselect. Review caught it. The label reads high rather than low as a result, which is
 * the open question already recorded for the PRD author.
 */
export const importCount = (groups, selection, autoLinkCount, createCount) =>
	(autoLinkCount || 0) + (createCount || 0) + toResolutions(groups, selection).length;
